import { saveLS, removeLS } from '#helper/localStorage'
import { captureError } from '#helper/sentry'

import checkToast from '#toast'

/**
 * Changes the selected camera device and updates localStorage accordingly.
 *
 * @param {String|null} id - The ID of the selected camera device. Use 'undefined' to clear the selection.
 * @returns {Object} - An object containing the updated state properties: { selectedDeviceId, startCam }
 */
export const changeSelected = (id) => {
	if (id === 'undefined') {
		removeLS('selectedCamera')
	} else {
		saveLS('selectedCamera', id)
	}
	return { selectedDeviceId: id, startCam: false }
}

// getUserMedia error names (DOMException.name) and their toast codes.
const CAMERA_ERROR_CODES = {
	NotAllowedError: 16003,
	SecurityError: 16003,
	NotReadableError: 16004,
	TrackStartError: 16004,
	AbortError: 16004,
	NotFoundError: 16005,
	DevicesNotFoundError: 16005,
	OverconstrainedError: 16006,
}

/**
 * Shows a toast with the reason a camera could not be opened and reports the error to Sentry.
 * The toast names the browser's error, so the user can send a screenshot.
 *
 * @param {Function} t - The translation function for localization.
 * @param {Error} error - The error from getUserMedia, enumerateDevices or Quagga.
 * @param {String} step - Where the error happened, for Sentry.
 * @returns {void}
 */
export const reportCameraError = (t, error, step) => {
	const name = error?.name || 'UnknownError'
	console.error(`Camera error (${step}): `, error)
	checkToast(t, CAMERA_ERROR_CODES[name] || 16001, undefined, { name })
	captureError(error, {
		step,
		errorName: name,
		errorMessage: error?.message,
		userAgent: navigator.userAgent,
		standalone:
			window.matchMedia?.('(display-mode: standalone)').matches ||
			window.navigator.standalone === true,
	})
}

/**
 * Asynchronously retrieves and returns the list of available video input devices.
 *
 * @async
 * @param {Function} t - The translation function for localization.
 * @returns {Promise<Array<Object>>} - A promise that resolves with an array of video input devices.
 */
export const getMediaDevices = async (t) => {
	if (!navigator.mediaDevices?.enumerateDevices) {
		checkToast(t, 16002)
		return []
	}
	try {
		// Device labels are only filled in after camera access was granted.
		const stream = await navigator.mediaDevices.getUserMedia({
			video: true,
			audio: false,
		})
		// Close the camera again, otherwise the scanner can't open it later.
		stream.getTracks().forEach((track) => track.stop())

		const devices = await navigator.mediaDevices.enumerateDevices()
		return devices.filter((device) => device.kind === 'videoinput')
	} catch (error) {
		reportCameraError(t, error, 'getMediaDevices')
		return []
	}
}

export default getMediaDevices
